// Admin page for editing email templates (subject + body in UZ/RU/EN).
import React, { useEffect, useState } from 'react';
import api from '../api/client';
import toast from 'react-hot-toast';

const PLACEHOLDER_LEGEND = `Mavjud o'zgaruvchilar (qo'sh-jingalak qavslar bilan ishlatiladi):
{{author_name}}, {{title}}, {{comment}}, {{deadline}}, {{article_url}}, {{doi}},
{{verify_url}}, {{reset_url}}, {{link}}, {{section}}, {{reviewer_name}}, {{result}}`;

export default function EmailTemplatesPage() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/email-templates').then((r) => {
      setItems(r.data.items || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const select = (t) => {
    setActive(t);
    setDraft({ ...t });
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const res = await api.put(`/admin/email-templates/${draft.key}`, {
        subject_uz: draft.subject_uz || '',
        subject_ru: draft.subject_ru || null,
        subject_en: draft.subject_en || null,
        body_uz: draft.body_uz || '',
        body_ru: draft.body_ru || null,
        body_en: draft.body_en || null,
        is_enabled: draft.is_enabled,
        description: draft.description || null,
      });
      setItems((prev) => prev.map((t) => (t.id === res.data.id ? res.data : t)));
      setActive(res.data);
      toast.success('Saqlandi');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Xatolik');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4 max-w-[1300px] mx-auto">
      {/* Sidebar */}
      <aside className="bg-white border border-slate-200 rounded-2xl p-3">
        <h2 className="text-sm font-bold text-slate-700 px-2 pb-2 border-b border-slate-100">
          Email shablonlar
        </h2>
        {loading ? (
          <div className="p-3 text-sm text-slate-400">Yuklanmoqda...</div>
        ) : (
          <ul className="mt-2 space-y-1">
            {items.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => select(t)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm ${
                    active?.id === t.id
                      ? 'bg-blue-50 text-blue-700 font-semibold'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <span className="font-mono text-xs block">{t.key}</span>
                  {t.description && (
                    <span className="text-[11px] text-slate-400 block truncate">
                      {t.description}
                    </span>
                  )}
                  {!t.is_enabled && (
                    <span className="inline-block text-[10px] mt-0.5 px-1.5 py-0.5 bg-rose-50 text-rose-600 rounded">
                      O'chirilgan
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>

      {/* Editor */}
      <section className="bg-white border border-slate-200 rounded-2xl p-5">
        {!draft ? (
          <div className="text-center py-16 text-slate-400">
            Chap tarafdan shablon tanlang
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-bold text-slate-900 font-mono">{draft.key}</h2>
              {draft.description && (
                <p className="text-xs text-slate-500 mt-1">{draft.description}</p>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!draft.is_enabled}
                onChange={(e) => setDraft({ ...draft, is_enabled: e.target.checked })}
              />
              Faol (yuborilsin)
            </label>

            <details className="bg-slate-50 rounded-lg p-3 text-xs">
              <summary className="cursor-pointer font-semibold text-slate-600">
                O'zgaruvchilar ro'yxati
              </summary>
              <pre className="mt-2 text-[11px] text-slate-600 whitespace-pre-wrap">
                {PLACEHOLDER_LEGEND}
              </pre>
            </details>

            {['uz', 'ru', 'en'].map((lang) => (
              <div key={lang} className="border-t border-slate-100 pt-3">
                <div className="text-xs font-bold text-slate-500 uppercase mb-2">{lang}</div>
                <input
                  type="text"
                  placeholder={`Subject (${lang})`}
                  value={draft[`subject_${lang}`] || ''}
                  onChange={(e) => setDraft({ ...draft, [`subject_${lang}`]: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mb-2"
                />
                <textarea
                  placeholder={`Body (${lang})`}
                  value={draft[`body_${lang}`] || ''}
                  onChange={(e) => setDraft({ ...draft, [`body_${lang}`]: e.target.value })}
                  rows={8}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono"
                />
              </div>
            ))}

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
              <button
                onClick={() => setDraft({ ...active })}
                className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm hover:bg-slate-200"
              >
                Bekor qilish
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saqlanmoqda...' : 'Saqlash'}
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
